import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { PenLine, X, Save, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function NoteTakingPad({ pageContext = 'general', userId = null }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [note, setNote] = useState('');
  const [savedNotes, setSavedNotes] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const textareaRef = useRef(null);

  // Load notes when panel opens
  useEffect(() => {
    if (isOpen && userId) {
      loadNotes();
    }
  }, [isOpen, userId]);

  // Auto-focus textarea when opened
  useEffect(() => {
    if (isOpen && !isMinimized && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isOpen, isMinimized]);

  const loadNotes = async () => {
    if (!userId) return;
    setIsLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/journal/${userId}`);
      if (response.ok) {
        const data = await response.json();
        // Filter notes for this page context
        const pageNotes = data.entries?.filter(entry => 
          entry.context === pageContext || entry.page_context === pageContext
        ) || [];
        setSavedNotes(pageNotes.slice(0, 5)); // Show last 5 notes
      }
    } catch (error) {
      console.log('Could not load notes');
    } finally {
      setIsLoading(false);
    }
  };

  const saveNote = async () => {
    if (!note.trim()) {
      toast.error('Please write something first');
      return;
    }

    setIsSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/journal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId || 'anonymous',
          entry_type: 'quick_note',
          content: note,
          context: pageContext,
          page_context: pageContext,
          metadata: {
            source: 'note_pad',
            timestamp: new Date().toISOString()
          }
        }),
      });

      if (response.ok) {
        toast.success('Note saved to your journal!');
        setNote('');
        if (userId) loadNotes(); // Refresh notes list
      } else {
        toast.error('Could not save note');
      }
    } catch (error) {
      toast.error('Could not save note');
    } finally {
      setIsSaving(false);
    }
  };

  const deleteNote = async (noteId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/journal/${noteId}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        setSavedNotes(savedNotes.filter(n => n._id !== noteId));
        toast.success('Note deleted');
      }
    } catch (error) {
      toast.error('Could not delete note');
    }
  };

  // Floating toggle button
  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed top-20 right-4 z-50 flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full shadow-lg hover:shadow-xl hover:from-amber-400 hover:to-orange-400 transition-all duration-300 group text-xs"
        data-testid="note-pad-toggle"
      >
        <PenLine className="w-3 h-3" />
        <span className="font-medium">Notes</span>
      </button>
    );
  }

  return (
    <div 
      className={`fixed top-16 right-4 z-50 w-80 md:w-96 transition-all duration-300 ${
        isMinimized ? 'h-12' : 'max-h-[70vh]'
      }`}
      data-testid="note-pad-panel"
    >
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl shadow-2xl border border-amber-500/30 overflow-hidden">
        {/* Header */}
        <div 
          className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-amber-600 to-orange-600 cursor-pointer"
          onClick={() => setIsMinimized(!isMinimized)}
        >
          <div className="flex items-center gap-2">
            <PenLine className="w-5 h-5 text-white" />
            <span className="font-semibold text-white">Quick Notes</span>
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={(e) => { e.stopPropagation(); setIsMinimized(!isMinimized); }}
              className="p-1 hover:bg-white/20 rounded transition-colors"
            >
              {isMinimized ? (
                <ChevronDown className="w-5 h-5 text-white" />
              ) : (
                <ChevronUp className="w-5 h-5 text-white" />
              )}
            </button>
            <button 
              onClick={(e) => { e.stopPropagation(); setIsOpen(false); }}
              className="p-1 hover:bg-white/20 rounded transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        {/* Content */}
        {!isMinimized && (
          <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
            {/* New Note Input */}
            <div className="space-y-3">
              <Textarea
                ref={textareaRef}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Write a quick note... it will be saved to your journal."
                className="bg-slate-800/50 border-slate-600 text-white placeholder:text-slate-400 min-h-[100px] resize-none focus:border-amber-500"
                data-testid="note-input"
              />
              <Button
                onClick={saveNote}
                disabled={isSaving || !note.trim()}
                className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white font-semibold"
              >
                {isSaving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save to Journal
                  </>
                )}
              </Button>
            </div>

            {/* Recent Notes */}
            {userId && savedNotes.length > 0 && (
              <div className="border-t border-slate-700 pt-4">
                <h4 className="text-sm font-semibold text-slate-400 mb-3">Recent Notes from this Page</h4>
                <div className="space-y-2">
                  {savedNotes.map((savedNote) => (
                    <div 
                      key={savedNote._id}
                      className="group bg-slate-800/50 rounded-lg p-3 border border-slate-700/50"
                    >
                      <p className="text-sm text-slate-300 line-clamp-2">{savedNote.content}</p>
                      <div className="flex justify-between items-center mt-2">
                        <span className="text-xs text-slate-500">
                          {new Date(savedNote.created_at || savedNote.timestamp).toLocaleDateString()}
                        </span>
                        <button
                          onClick={() => deleteNote(savedNote._id)}
                          className="opacity-0 group-hover:opacity-100 p-1 text-red-400 hover:text-red-300 transition-opacity"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Hint */}
            <p className="text-xs text-slate-500 text-center">
              Notes are saved to your personal journal
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
